import React, { useEffect, useMemo, useState } from "react";
import { Card, List, message, Empty } from "antd";
import "dayjs/locale/en-gb";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks.ts";
import { fetchFolderHistory } from "../../../redux/actions/folderActions.tsx";
import { Loading } from "../../common/Loading.tsx";
import { HistoryItem } from "./HistoryItem.tsx";
import { HistoryRecord } from "../../../redux/interfaces/folder.types.ts";
import { HistoryFilters } from "./HistoryFilters.tsx";
import { extractErrorMessage } from "../../../utils/extractErrorMessage.ts";

interface FilterState {
  roles: string[];
  resourceTypes: string[];
  subfolderSlugs: string[];
  requirementSlugs: string[];
}

export const History: React.FC = () => {
  const dispatch = useAppDispatch();
  const { activeFolder, activeFolderHistory, loading } = useAppSelector(
    (state) => state.folder,
  );
  const [messageApi, contextHolder] = message.useMessage();

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    roles: [],
    resourceTypes: [],
    subfolderSlugs: [],
    requirementSlugs: [],
  });

  // Convert activeFolderHistory to the expected format
  const historyRecords: HistoryRecord[] = useMemo(() => {
    return Array.isArray(activeFolderHistory)
      ? activeFolderHistory
      : activeFolderHistory?.changes?.map((change) => ({
          id: change.timestamp,
          created_at: change.timestamp,
          resource_type: change.resource,
          resource_id: "",
          resource_slug: null,
          name: change.name,
          action: change.action,
          author: change.author,
          role: change.role,
          previous_status: change.status?.previous || null,
          new_status: change.status?.actual || null,
          previous_is_active: change.is_active?.previous || null,
          new_is_active: change.is_active?.actual || null,
          previous_assigned_user: change.assigned_user?.previous || null,
          new_assigned_user: change.assigned_user?.actual || null,
          previous_collaborator: change.collaborator?.previous || null,
          new_collaborator: change.collaborator?.actual || null,
          previous_file: null,
          new_file: null,
          extra_data: null,
        })) || [];
  }, [activeFolderHistory]);

  // Extract unique values for filter options, separated by resource type
  const filterOptions = useMemo(() => {
    const roles = [...new Set(historyRecords.map((item) => item.role))].sort();
    const resourceTypes = [
      ...new Set(historyRecords.map((item) => item.resource_type)),
    ].sort();

    // Separate slugs by resource type
    const subfolderSlugs = [
      ...new Set(
        historyRecords
          .filter(
            (item) => item.resource_type === "Subfolder" && item.resource_slug,
          )
          .map((item) => item.resource_slug as string),
      ),
    ].sort();

    const requirementSlugs = [
      ...new Set(
        historyRecords
          .filter(
            (item) =>
              item.resource_type === "Requirement" && item.resource_slug,
          )
          .map((item) => item.resource_slug as string),
      ),
    ].sort();

    return { roles, resourceTypes, subfolderSlugs, requirementSlugs };
  }, [historyRecords]);

  const filteredRecords = useMemo(() => {
    return historyRecords.filter((record) => {
      const roleMatch =
        filters.roles.length === 0 || filters.roles.includes(record.role);
      const resourceTypeMatch =
        filters.resourceTypes.length === 0 ||
        filters.resourceTypes.includes(record.resource_type);

      let slugMatch = true;

      // If we have subfolder slug filters, check if this record matches
      if (filters.subfolderSlugs.length > 0) {
        const subfolderSlugMatch =
          record.resource_type === "Subfolder" &&
          !!record.resource_slug &&
          filters.subfolderSlugs.includes(record.resource_slug);

        // If we also have requirement slug filters, use OR logic
        if (filters.requirementSlugs.length > 0) {
          const requirementSlugMatch =
            record.resource_type === "Requirement" &&
            !!record.resource_slug &&
            filters.requirementSlugs.includes(record.resource_slug);
          slugMatch = subfolderSlugMatch || requirementSlugMatch;
        } else {
          slugMatch = subfolderSlugMatch;
        }
      }
      // If we only have requirement slug filters
      else if (filters.requirementSlugs.length > 0) {
        slugMatch =
          record.resource_type === "Requirement" &&
          !!record.resource_slug &&
          filters.requirementSlugs.includes(record.resource_slug);
      }

      return roleMatch && resourceTypeMatch && slugMatch;
    });
  }, [historyRecords, filters]);

  const onFetchFolderHistory = async () => {
    if (!activeFolder) return;
    try {
      await dispatch(fetchFolderHistory(activeFolder.id)).unwrap();
    } catch (error: any) {
      messageApi.error(extractErrorMessage(error, "No se pudo cargar el historial."));
    }
  };

  useEffect(() => {
    onFetchFolderHistory();
  }, [activeFolder]);

  if (loading) return <Loading />;

  return (
    <div className="p-6 bg-gray-50 min-h-96 shadow-none h-96 overflow-auto">
      {contextHolder}

      {/* Filters */}
      {historyRecords.length > 0 && (
        <HistoryFilters
          availableRoles={filterOptions.roles}
          availableResourceTypes={filterOptions.resourceTypes}
          availableSubfolderSlugs={filterOptions.subfolderSlugs}
          availableRequirementSlugs={filterOptions.requirementSlugs}
          filters={filters}
          onFiltersChange={setFilters}
          totalCount={historyRecords.length}
          filteredCount={filteredRecords.length}
        />
      )}

      <Card bordered={false}>
        {filteredRecords.length === 0 ? (
          <Empty
            description={
              historyRecords.length === 0
                ? "No hay registros de historial disponibles"
                : "No se encontraron registros con los filtros aplicados"
            }
            style={{ padding: "40px 0" }}
          />
        ) : (
          <List
            itemLayout="vertical"
            bordered
            dataSource={filteredRecords}
            renderItem={(record) => <HistoryItem record={record} />}
          />
        )}
      </Card>
    </div>
  );
};
